import { deleteOrder, updateOrder } from 'api/accessexpressv3/api';
import clsx from 'clsx';
import { GraniteButton } from 'components/V2/Button/GraniteButton';
import { useFeatureFlags } from 'feature-flags';
import { observer } from 'mobx-react-lite';
import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useCartContext } from 'screens/AccessExpressV3/CartProvider';

const CartContainer = observer(
  ({
    children,
    isOpen,
    onClick,
    isElevated,
  }: {
    children: React.ReactNode;
    isOpen: boolean;
    onClick: () => void;
    isElevated?: boolean;
  }) => {
    const { flags } = useFeatureFlags();
    const location = useLocation();
    const isCheckoutPage = location.pathname.includes('/checkout');

    const cart = useCartContext();
    const handleCleanupLocations = useCallback(async () => {
      if (!cart.draftId) return;

      const draft = await updateOrder({ locations: [] }, cart.draftId);

      cart.updateStateFromDraft(draft);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cart.draftId]);

    const handleDeleteDraftOrder = useCallback(async () => {
      if (!cart.draftId) return;

      await deleteOrder(cart.draftId);
      cart.reset();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cart.draftId, cart.reset]);

    // const [mixedCartFeature, setMixedCartFeature] = useLocalStorageState<
    //   'on' | 'off'
    // >('mixed-cart', 'off');

    return (
      <div
        className={clsx(
          'flex-none overflow-hidden bg-background-base-surface-1 shadow-[-15px_4px_24px_0px_rgba(0,0,0,0.25)] transition-all duration-700',
          'fixed right-0 top-16 z-[49] xl:sticky xl:top-0',
          'h-[calc(100vh-64px)] max-h-[calc(100vh-64px)] xl:h-full xl:max-h-screen',
          isOpen ? 'w-full xs:w-[400px]' : 'w-[64px]',
          isElevated &&
            'bg-[linear-gradient(0deg,_rgba(126,119,179,0.04)_0%,_rgba(126,119,179,0.04)_100%)] shadow-[-15px_4px_24px_0px_rgba(126,119,179,0.16)]',
        )}
      >
        {isElevated && (
          <div className="absolute left-0 top-0 h-full w-px bg-accent-purple"></div>
        )}
        <div
          className={clsx(
            'flex h-full flex-col  overflow-hidden px-6 py-8 transition-all duration-700',
            isOpen ? 'w-full xs:w-[400px]' : 'w-[64px]',
            { 'gap-[56px]': isCheckoutPage, 'gap-[92px]': !isCheckoutPage },
          )}
        >
          {children}
        </div>
        <div
          onClick={onClick}
          className={clsx(
            'absolute bottom-0 left-0 top-0 w-[64px]',
            isOpen ? 'pointer-events-none' : 'cursor-pointer',
          )}
        ></div>
        {flags.DEVELOPER_MODE && (
          <div
            className={clsx(
              'absolute bottom-1.5 left-0 right-0 flex w-[400px] items-center justify-center gap-0 whitespace-nowrap text-xs transition-opacity duration-500 ease-out',
              isOpen ? 'opacity-100' : 'opacity-0',
            )}
          >
            <span>(dev) </span>
            <GraniteButton
              variant="unstyled"
              size="small"
              className=""
              onClick={() => handleCleanupLocations()}
            >
              Cleanup Locations
            </GraniteButton>
            <GraniteButton
              variant="unstyled"
              size="small"
              className=""
              onClick={() => handleDeleteDraftOrder()}
            >
              Delete Draft Order
            </GraniteButton>
          </div>
        )}
      </div>
    );
  },
);

export default CartContainer;
